import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import swimService from '../api/services/swimService';
import { SwimEnrollment } from '../types/swim';

function hoursSince(iso: string | null): number | null {
  if (!iso) return null;
  const diffMs = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diffMs)) return null;
  return diffMs / (1000 * 60 * 60);
}

const PendingEnrollmentsAdminPage: React.FC = () => {
  const [items, setItems] = useState<SwimEnrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => { void load(); }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const rows = await swimService.listPending();
      setItems(rows);
      setSelected(new Set());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load pending enrollments');
    } finally {
      setLoading(false);
    }
  }

  async function confirmOne(id: string) {
    setBusy(true);
    try {
      await swimService.confirmEnrollment(id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Confirm failed');
    } finally {
      setBusy(false);
    }
  }

  async function bulkConfirm() {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const res = await swimService.bulkConfirmEnrollments(Array.from(selected));
      setError(null);
      await load();
      // simple inline feedback via temporary error slot
      setError(`Confirmed ${res.confirmed}, skipped ${res.skipped}.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Bulk confirm failed');
    } finally {
      setBusy(false);
    }
  }

  const sorted = useMemo(
    () => [...items].sort((a, b) => (a.enrolled_at ?? '').localeCompare(b.enrolled_at ?? '')),
    [items],
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" component="h1">Confirm Sign-ups</Typography>
          <Typography variant="body2" color="text.secondary">
            Confirm that swimmers are signed up — this adds them to the coach's roster.
            Payment is handled separately (front desk / monthly bill). Unconfirmed sign-ups
            auto-expire after 72 hours.
          </Typography>
        </Box>
        <IconButton onClick={() => void load()} disabled={loading || busy}>
          <RefreshIcon />
        </IconButton>
      </Stack>

      {error && <Alert severity={error.startsWith('Confirmed') ? 'success' : 'error'} sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined">
        <Toolbar sx={{ minHeight: '56px !important' }}>
          <Typography sx={{ flexGrow: 1 }} variant="subtitle1">
            {sorted.length} pending
            {selected.size > 0 && ` — ${selected.size} selected`}
          </Typography>
          <Button
            startIcon={<CheckCircleIcon />}
            variant="contained"
            disabled={selected.size === 0 || busy}
            onClick={() => void bulkConfirm()}
          >
            Confirm {selected.size > 0 ? `(${selected.size})` : 'selected'}
          </Button>
        </Toolbar>
        <TableContainer>
          {loading ? (
            <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
          ) : sorted.length === 0 ? (
            <Box p={4} textAlign="center">
              <Typography color="text.secondary">No pending enrollments.</Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selected.size > 0 && selected.size < sorted.length}
                      checked={selected.size === sorted.length && sorted.length > 0}
                      onChange={(e) =>
                        setSelected(e.target.checked ? new Set(sorted.map((r) => r.id)) : new Set())
                      }
                    />
                  </TableCell>
                  <TableCell>Swimmer</TableCell>
                  <TableCell>Program</TableCell>
                  <TableCell>Enrolled</TableCell>
                  <TableCell>Age of pending</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((e) => {
                  const swimmer = e.enrollee
                    ? `${e.enrollee.first_name ?? ''} ${e.enrollee.last_name ?? ''}`.trim() || '—'
                    : '—';
                  const hrs = hoursSince(e.enrolled_at);
                  const ageBadgeColor: 'default' | 'warning' | 'error' =
                    hrs == null ? 'default' : hrs > 48 ? 'error' : hrs > 24 ? 'warning' : 'default';
                  return (
                    <TableRow key={e.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected.has(e.id)}
                          onChange={(ev) => {
                            const next = new Set(selected);
                            if (ev.target.checked) next.add(e.id);
                            else next.delete(e.id);
                            setSelected(next);
                          }}
                        />
                      </TableCell>
                      <TableCell>{swimmer}</TableCell>
                      <TableCell>{e.program?.name ?? '—'}</TableCell>
                      <TableCell>
                        {e.enrolled_at ? new Date(e.enrolled_at).toLocaleString() : '—'}
                      </TableCell>
                      <TableCell>
                        {hrs != null && (
                          <Chip
                            size="small"
                            label={`${hrs.toFixed(1)}h`}
                            color={ageBadgeColor}
                            variant={ageBadgeColor === 'default' ? 'outlined' : 'filled'}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">${Number(e.amount_due).toFixed(2)}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={busy}
                          onClick={() => void confirmOne(e.id)}
                        >
                          Confirm
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default PendingEnrollmentsAdminPage;
